<template>
  <MPopover
    :ref="`scrollable-dropdown-${guid}`"
    :placement="popoverPlacment"
    :get-popup-container="getPopupContainer"
    v-bind="$attrs"
    :disabled="disabled"
    transition-name="slide-up"
    :overlay-class-name="overlayClassName"
    @hide="handleHide"
    @show="handleShow"
    v-on="listeners"
  >
    <template v-slot:trigger>
      <slot
        name="trigger"
        :hide="handleHide"
        :show="handleShow"
        :toggle="toggleDropdown"
      />
    </template>
    <div class="flex flex-col h-100 min-h-0">
      <div ref="scrollContainer" class="flex h-100 min-h-0 flex-col">
        <slot name="before-menu"></slot>
        <FlotoScrollView>
          <slot>
            <MMenu v-if="options.length" ref="menuContainer" :class="menuClass">
              <MMenuItem
                v-for="(item, index) in optionsToDisplay"
                :key="String(item.id || item.value || item.key)"
                :class="{
                  'scroll-dropdown-menu-item': true,
                  [menuItemClass]: true,
                  [menuItemSelectedClass]: index === currentIndex,
                  [menuItemDisabledClass]: item.disabled,
                  'value-active-item': Array.isArray(value)
                    ? value.indexOf(item.key) >= 0
                    : value === item.key,
                }"
              >
                <slot name="menu-item" :item="item" :select-item="selectItem">
                  <div class="text-ellipsis" @click.stop="selectItem(item)">
                    {{ item.text || item.label || item.name }}
                  </div>
                </slot>
              </MMenuItem>
            </MMenu>
            <slot
              v-if="showNoData && optionsToDisplay.length <= 0"
              name="no-data"
            >
              <FlotoNoData
                v-if="showNoData && optionsToDisplay.length <= 0"
                size="small"
              />
            </slot>
          </slot>
        </FlotoScrollView>
        <slot name="after-menu"></slot>
      </div>
    </div>
  </MPopover>
</template>

<script>
import FindIndex from 'lodash/findIndex'
import Mousetrap from 'mousetrap'
import Bus from '@utils/emitter'
import { authComputed } from '@state/modules/auth'
import { generateId } from '@utils/id'

export default {
  name: 'FlotoScrollDropdown',
  inject: {
    searchBarContext: {
      default: () => ({
        pickerdata: [],
      }),
    },
    popupContext: {
      default: () => ({
        popupManagerData: () => [],
      }),
    },
  },
  model: {
    event: 'change',
  },
  props: {
    value: {
      type: [String, Number, Array, Object, Boolean],
      default: undefined,
    },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    avoidKeyboardNavigation: { type: Boolean, default: false },
    // eslint-disable-next-line
    showNoData: { type: Boolean, default: true },
    options: {
      type: Array,
      default() {
        return []
      },
    },
    filteredOptions: {
      type: Array,
      default: undefined,
    },
    getPopupContainer: { type: Function, default: undefined },
    overlayClassName: { type: String, default: 'picker-overlay' },
  },
  data() {
    this.guid = generateId()
    // @TODO make this classes dynamic somehow
    this.menuClass = 'ant-dropdown-menu'
    this.menuItemClass = 'ant-dropdown-menu-item'
    this.menuItemDisabledClass = 'ant-dropdown-menu-item-disabled'
    this.menuItemSelectedClass = 'ant-dropdown-menu-item-selected'
    let initialIndex
    if (this.avoidKeyboardNavigation) {
      initialIndex = undefined
    } else {
      initialIndex = FindIndex(this.options, { key: this.value })
    }
    return {
      initialFocusedElement: null,
      currentIndex: initialIndex === -1 ? 0 : initialIndex,
      isDropdownOpen: false,
    }
  },
  computed: {
    ...authComputed,
    popoverPlacment() {
      return this.isRtl ? 'bottomRight' : 'bottomLeft'
    },
    optionsToDisplay() {
      let options = this.options
      const value = this.multiple ? this.value || [] : this.value
      if (this.filteredOptions) {
        options = this.filteredOptions
      }
      if (Array.isArray(value) && value.length) {
        options = options.filter(
          (o) => value.indexOf(o.key || o.id || o.value) === -1
        )
      }
      return options
    },
    listeners() {
      const { change, show, hide, ...listeners } = this.$listeners
      return listeners
    },
  },
  watch: {
    options(newValue, oldValue) {
      if (newValue !== oldValue) {
        window.dispatchEvent(new Event('resize'))
      }
    },
  },
  created() {
    this.upHandler = this.upHandler.bind(this)
    this.downHandler = this.downHandler.bind(this)
    this.enterHandler = this.enterHandler.bind(this)
    this.keyupHandler = this.keyupHandler.bind(this)
  },
  mounted() {
    if (!this.avoidKeyboardNavigation) {
      this.$once('hook:beforeDestroy', () => {
        this.unbindEvents()
      })
    }
  },
  methods: {
    bindEvents() {
      if (this.avoidKeyboardNavigation) {
        return
      }
      Mousetrap.unbind(['up', 'down', 'enter'])
      document.addEventListener('keyup', this.keyupHandler)
    },
    keyupHandler(e) {
      switch (e.key) {
        case 'ArrowUp':
          this.upHandler(e)
          break
        case 'ArrowDown':
          this.downHandler(e)
          break
        case 'Enter':
          this.enterHandler(e)
          break
        default:
          break
      }
    },
    unbindEvents() {
      if (this.avoidKeyboardNavigation) {
        return
      }
      document.removeEventListener('keyup', this.keyupHandler)
      Mousetrap.bind(['up', 'down', 'enter'], (...args) => {
        const command =
          args[0].key === 'ArrowUp'
            ? 'move_up'
            : args[0].key === 'ArrowDown'
            ? 'move_down'
            : 'enter'
        Bus.$emit(command, ...[...args, command])
      })
    },
    resetCurrentIndex() {
      if (this.avoidKeyboardNavigation) {
        return
      }
      const initialIndex = FindIndex(this.optionsToDisplay, { key: this.value })
      this.currentIndex = initialIndex === -1 ? 0 : initialIndex
      this.$emit('active-item-index-change', this.currentIndex)
    },
    scrollActiveItemToView() {
      if (
        this.$refs.menuContainer &&
        this.$refs.menuContainer.$el.offsetHeight >
          this.$refs.scrollContainer.offsetHeight
      ) {
        const activeMenuItemDom =
          this.$refs.menuContainer.$el.childNodes[this.currentIndex]
        if (activeMenuItemDom) {
          activeMenuItemDom.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }
    },
    focusOut() {
      if (this.initialFocusedElement && !this.focusEventBrodcast) {
        this.initialFocusedElement.focus()
      }
    },
    handleHide($event) {
      this.$emit('hide', $event)
      this.focusOut()
      this.unbindEvents()
      if (this.$refs[`scrollable-dropdown-${this.guid}`]) {
        this.$refs[`scrollable-dropdown-${this.guid}`].hide()
      }

      const dropdownRef = `scrollable-dropdown-${this.guid}`

      if (this.popupContext && this.popupContext.popupManagerData.length) {
        const index = this.popupContext.popupManagerData.findIndex(
          (item) => Object.keys(item)[0] === dropdownRef
        )

        if (index !== -1) {
          // Use splice to remove the element at the found index
          this.popupContext.popupManagerData.splice(index, 1)
        }
      }
      setTimeout(() => {
        this.isDropdownOpen = false
      }, 350)
    },
    handleShow($event) {
      this.initialFocusedElement = document.activeElement
      if (this.disabled) {
        return
      }
      this.resetCurrentIndex()
      this.bindEvents()

      const keyExists = () => {
        const popupManagerData = this.popupContext.popupManagerData || []
        return popupManagerData.some((item) =>
          item.hasOwnProperty(`scrollable-dropdown-${this.guid}`)
        )
      }

      if (
        this.searchBarContext &&
        this.popupContext.popupManagerData &&
        !keyExists()
      ) {
        this.popupContext.popupManagerData.push({
          [`scrollable-dropdown-${this.guid}`]: this.handleHide,
        })
      }
      this.$emit('show', $event)
      this.isDropdownOpen = true
    },
    toggleDropdown() {
      if (this.isDropdownOpen) {
        this.handleHide()
      } else {
        this.handleShow()
      }
    },
    upHandler(e) {
      e.stopImmediatePropagation()
      e.preventDefault()
      e.stopPropagation()
      requestAnimationFrame(() => {
        this.currentIndex =
          (this.currentIndex + this.optionsToDisplay.length - 1) %
          this.optionsToDisplay.length
        this.$emit('active-item-index-change', this.currentIndex)
        this.scrollActiveItemToView()
      })
    },
    downHandler(e) {
      e.stopImmediatePropagation()
      e.preventDefault()
      e.stopPropagation()
      requestAnimationFrame(() => {
        this.currentIndex =
          (this.currentIndex + 1) % this.optionsToDisplay.length
        this.$emit('active-item-index-change', this.currentIndex)
        this.scrollActiveItemToView()
      })
      return true
    },
    enterHandler(e) {
      e.stopImmediatePropagation()
      e.stopPropagation()
      e.preventDefault()
      if (this.optionsToDisplay.length) {
        // here the item is selected
        requestAnimationFrame(() => {
          this.selectItem(this.optionsToDisplay[this.currentIndex])
        })
      }
    },
    selectItem(item) {
      const value = this.multiple ? this.value || [] : this.value
      if (Array.isArray(value)) {
        if (value.indexOf(item.key) === -1) {
          this.$emit('change', [...value, item.value || item.id || item.key])
        } else {
          this.$emit(
            'change',
            value.filter((i) => i !== item.value || item.id || item.key)
          )
        }
      } else {
        this.$emit('change', item.value || item.id || item.key)
        this.hide()
      }
      // this.$emit('change', item)
    },
    show() {
      // if (this.$refs.popoverMenu) {
      //   this.$refs.popoverMenu.show()
      // }
      this.handleShow()
    },
    hide() {
      // if (this.$refs.popoverMenu) {
      //   this.$refs.popoverMenu.hide()
      // }
      this.handleHide()
    },
  },
}
</script>

<style lang="less" scoped>
// .scroll-dropdown-menu-item {
//   color: var(--dropdown-text);
//   &.value-active-item {
//     font-weight: bold;
//     color: var(--dropdown-selected-text);
//     background: var(--dropdown-hover-bg);
//   }
// }
</style>

<style lang="less">
.scroll-dropdown-menu-item {
  a {
    color: inherit;
  }
}
</style>
